
import { defineComponent, PropType } from 'vue';
import { Notification, NotificationAction } from './types.ts';

export default defineComponent({
  name: 'NotificationItem',
  props: {
    notification: {
      type: Object as PropType<Notification>,
      required: true,
    },
  },
  data() {
    return {
      isPaused: false,
      progressWidth: 100,
      timer: null as number | null,
      progressTimer: null as number | null,
    };
  },
  computed: {
    notificationClasses(): string[] {
      const classes = ['notification', `notification--${this.notification.type}`];
      
      if (this.notification.read) {
        classes.push('notification--read');
      }
      
      return classes;
    },
    
    notificationStyles(): Record<string, string> {
      return {
        maxWidth: this.maxWidth,
      };
    },
    
    progressStyles(): Record<string, string> {
      return {
        width: `${this.progressWidth}%`,
        backgroundColor: this.progressColor,
      };
    },
    
    icon(): string {
      const icons = {
        success: 'mdi-check-circle',
        error: 'mdi-alert-circle',
        warning: 'mdi-alert',
        info: 'mdi-information',
        critical: 'mdi-alert-octagon',
      };
      
      return icons[this.notification.type] || 'mdi-information';
    },
    
    iconColor(): string {
      const colors = {
        success: '#10b981',
        error: '#ef4444',
        warning: '#f59e0b',
        info: '#3b82f6',
        critical: '#8b5cf6',
      };
      
      return colors[this.notification.type] || '#3b82f6';
    },
    
    actionColor(): string {
      return this.notification.type === 'warning' ? 'warning' : this.iconColor;
    },
    
    progressColor(): string {
      return this.iconColor;
    },
    
    maxWidth(): string {
      const widths = {
        critical: '400px',
        error: '350px',
        warning: '350px',
        success: '320px',
        info: '320px',
      };
      
      return widths[this.notification.type] || '320px';
    },
    
    showProgress(): boolean {
      return !this.notification.persistent && this.duration > 0;
    },
    
    duration(): number {
      if (this.notification.persistent) return 0;
      
      const defaultDurations = {
        critical: 0,
        error: 10000,
        warning: 8000,
        success: 5000,
        info: 5000,
      };
      
      return this.notification.duration || defaultDurations[this.notification.type] || 5000;
    },
    
    ariaLabel(): string {
      const typeLabels = {
        success: 'Success',
        error: 'Error',
        warning: 'Warning',
        info: 'Information',
        critical: 'Critical',
      };
      
      const typeLabel = typeLabels[this.notification.type] || 'Notification';
      return `${typeLabel}: ${this.notification.title}${this.notification.message ? `. ${this.notification.message}` : ''}`;
    },
  },
  mounted() {
    this.startTimer();
  },
  beforeUnmount() {
    this.clearTimers();
  },
  methods: {
    startTimer() {
      if (this.duration <= 0) return;
      
      const startTime = Date.now();
      const updateProgress = () => {
        if (!this.isPaused) {
          const elapsed = Date.now() - startTime;
          const remaining = Math.max(0, this.duration - elapsed);
          this.progressWidth = (remaining / this.duration) * 100;
          
          if (remaining <= 0) {
            this.dismiss();
          } else {
            this.progressTimer = requestAnimationFrame(updateProgress);
          }
        } else {
          this.progressTimer = requestAnimationFrame(updateProgress);
        }
      };
      
      this.progressTimer = requestAnimationFrame(updateProgress);
    },
    
    pauseTimer() {
      this.isPaused = true;
    },
    
    resumeTimer() {
      this.isPaused = false;
    },
    
    clearTimers() {
      if (this.timer) {
        clearTimeout(this.timer);
        this.timer = null;
      }
      
      if (this.progressTimer) {
        cancelAnimationFrame(this.progressTimer);
        this.progressTimer = null;
      }
    },
    
    dismiss() {
      this.clearTimers();
      this.$emit('dismiss', this.notification.id);
    },
    
    handleClick() {
      this.$emit('click', this.notification);
    },
    
    handleAction(action: NotificationAction) {
      if (action.handler) {
        action.handler();
      }
    },
    
    handleKeydown(event: KeyboardEvent) {
      if (event.key === 'Escape' || event.key === 'Enter') {
        this.dismiss();
      }
    },
  },
});
